import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useRouter } from 'found';

import { Button, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useReviewContext, useErrorContext } from '@hydra/common/context';
import { Icon, Image, Translate, Layout } from '@hydra/common/components';
import { ErrorHar } from '@hydra/common/error';

import { useAuthenticationRoutes, usePricingRoutes, useI18n } from '@hydra/common/customHooks';

import { createFreeReview } from '@hydra/utils/reviewApi';
import redirectToUrl from '@hydra/utils/redirectToUrl';

import notFoundImg from '@hydra/assets/img/error/4xx_3.svg';

const useStyles = makeStyles(theme => ({
  title: {
    color: theme.palette.grey[900],
    margin: 0,
    textAlign: 'center'
  },
  root: {
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  layout: {
    display: 'flex'
  },
  textCenter: {
    textAlign: 'center'
  },
  buttonContainer: {
    textAlign: 'center',
    marginTop: theme.spacing(2)
  },
  notFoundWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },
  notFoundTitle: {
    marginBottom: theme.spacing(5)
  },
  notFoundImage: {
    maxWidth: '100%',
    flex: 1,
    marginRight: -30
  }
}));

export const ErrorPage = ({ error, sideNav }) => {
  const { match: { location } } = useRouter();
  const { isFree, isReview, hasReviewTeaserUrl } = useReviewContext();
  const url = location.pathname.split('/')[3];
  const { translate, locale } = useI18n();
  const { setError } = useErrorContext();
  const classes = useStyles();
  const [firstGenerationLoading, setFirstGenerationLoading] = useState(false);
  const redirectUrl = hasReviewTeaserUrl ? 'teaser-review' : 'www';
  const { signInRoute } = useAuthenticationRoutes();
  const { getPricingPageRoute } = usePricingRoutes();

  const firstGeneration = async (refreshReview) => {
    setFirstGenerationLoading(true);
    try {
      const { review, generation } = await createFreeReview(url, refreshReview);
      if (generation) {
        const searchParams = {
          generationId: generation.id,
          ...hasReviewTeaserUrl && { usecase: 'all' }
        };
        redirectToUrl({ url: `/${locale}/${redirectUrl}/${encodeURIComponent(review.url)}?${new URLSearchParams(searchParams)}` });
      }
    } catch (err) {
      const { errorCode, resolve } = err;
      if (errorCode === 'EXISTS') {
        redirectToUrl({ url: `/${locale}/${redirectUrl}/${encodeURIComponent(url)}` });
      } else if (errorCode === 'UNRESOLVABLE_URL') {
        setError({ errorCode, resolve });
      } else if (errorCode === 'RATE_LIMITED') {
        redirectToUrl({ url: getPricingPageRoute({ queryString: [{ error_ratelimited: '1' }] }) });
      } else {
        setError({ statusCode: 500 });
      }
    }
  };

  const displayErrorMessage = () => {
    if (error.resolve) {
      return <ErrorHar resolve={error.resolve} />;
    } else if ((error.message === 'not_found' || error.message === 'missing_data') && isFree) {
      const refresh = error.message === 'missing_data';
      return (
        <>
          <Translate
            className={classes.title}
            component='h2'
            content='first_generation_review_confirmation_title'
          />
          <Translate
            component='p'
            className={classes.textCenter}
            content='first_generation_review_confirmation_question'
            with={{ url }}
          />
          <div className={classes.buttonContainer}>
            <Button
              variant='contained'
              color='primary'
              startIcon={firstGenerationLoading && <Icon type='loader' color='white' size='sm' />}
              onClick={() => firstGeneration(refresh)}
            >
              <Translate content='first_generation_review_confirmation_btn' />
            </Button>
          </div>
        </>
      );
    } else if (error.message === 'not_found') {
      const textContent = isReview ? 'review_not_generated_before' : 'project_not_created_before';
      const buttonContent = isReview ? 'generate_review_now_button' : 'create_project_now_button';
      return (
        <>
          <Translate className={classes.title} component='h2' content={textContent} with={{ url }} />
          <div className={classes.buttonContainer}>
            <Button
              variant='contained'
              color='primary'
              href={`/${locale}/create/${isReview ? 'review' : 'project'}?url=${encodeURIComponent(url)}`}
            >
              <Translate content={buttonContent} />
            </Button>
          </div>
        </>
      );
    } else if (error.message === 'invalid_url' || error instanceof URIError) {
      return (
        <div className={classes.notFoundWrapper}>
          <Translate className={classes.notFoundTitle} component='h1' content='error_404' />
          <Image className={classes.notFoundImage} width='495' height='389' src={notFoundImg} alt='' />
        </div>
      );
    } else {
      return (
        <>
          <Translate className={classes.title} component='h1' content={`error_${error.statusCode || '500'}`} />
          {(error.statusCode === 403 && error.message !== 'page_permission_denied') && (
            <div className={classes.buttonContainer}>
              <Button
                variant='contained'
                color='primary'
                href={`${signInRoute}?redirect-url=${location.pathname}`}
              >
                <Translate content='log_in' />
              </Button>
            </div>
          )}
        </>
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${translate('generic_error')} | WooRank.com`}</title>
      </Helmet>
      <Layout sideNav={sideNav} className={classes.layout}>
        <Container
          maxWidth='lg'
          className={classes.root}
        >
          {displayErrorMessage()}
        </Container>
      </Layout>
    </>
  );
};

ErrorPage.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
    statusCode: PropTypes.number,
    resolve: PropTypes.object
  })
};

export default ErrorPage;
